import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import {
  BookingParty,
  BuyersConsolidationQuoteServicesJSON,
  CargoRoute,
  IQuoteRequest,
  useQuoteService,
} from 'features/BookingFlow'
import { ShipmentMode } from 'features/ShipmentDetails'
import { get } from 'lodash-es'
import { FC, ReactNode, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useAnalytics } from 'service/useAnalytics'
import { useRootStore } from 'service/useRootStore'
import { useI18n } from 'utils/localisation'
import { logError } from 'utils/logger'
import { ErrorBoundary } from 'view/ErrorBoundary'

import { ManualQuoteAdditionalFields } from '../ManualQuote/ManualQuoteAdditionalFields'
import { getManualQuoteFormContent } from './getManualQuoteFormContent'
import { DocumentInformation, useManualQuoteDocumentsApi } from './ManualQuoteDocumentUpload/manualQuoteDocumentsApi'
import { ManualQuoteDocumentUploadComponent } from './ManualQuoteDocumentUpload/ManualQuoteDocumentUploadComponent'
import { MQCommonFieldsFormSchema, MQFormCommonFields } from './MQFormCommonFields'
import { AdditionalRequirements, MQFields } from './MQFormContentProcessor'
import { MQServices } from './MQServices'
import { ManualQuoteDialogState, ManualQuoteType } from './useManualQuoteDialogState'

type CreateMQApiArgs = {
  data: MQFields
  files: DocumentInformation[]
}
type CreateMQApiProp = {
  createMQApi: (args: CreateMQApiArgs) => void
}

export type MQDialogContentProps = CreateMQApiProp & {
  quoteRequest: IQuoteRequest
  manualQuoteReasonMessage: string | undefined
  onCancelClick: () => void
  sendAnalyticsEvent: (e: string) => void
  isSubmitting: boolean
  mqDialogType: ManualQuoteType
  isSuccess: boolean
  userEmail: string
  salesManagerEmail?: string
  defaultFiles?: DocumentInformation[]
  issAdditionalFieldsExpanded?: boolean
}

type ManualQuotePayload = {
  quoteSheetId: string
  customerNotes: string
  modes?: ShipmentMode[]
  pickupAddress?: string
  deliveryAddress?: string
  cargoReadyAt?: Date
  additionalRequirements?: AdditionalRequirements
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
  incoterm?: string
  bookingParty?: string
}

const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(1),
  },
}))

const documentUploadMetaDataMapper = (input: DocumentInformation) => {
  return {
    state: 'published' as const,
    mimeType: input.fileInstance.type,
    customerID: input.customer_id,
    source: input.source,
    documentTypeCode: input.documentTypeCode,
  }
}

export const MQDialogContent: FC<MQDialogContentProps> = ({
  quoteRequest,
  onCancelClick,
  createMQApi,
  sendAnalyticsEvent,
  isSubmitting,
  mqDialogType,
  isSuccess,
  userEmail,
  salesManagerEmail,
  defaultFiles = [],
  issAdditionalFieldsExpanded = false,
  manualQuoteReasonMessage,
}) => {
  const { translate } = useI18n()
  const classes = useStyles()
  const [files, setFiles] = useState<DocumentInformation[]>(defaultFiles)

  const formContent = getManualQuoteFormContent({
    quoteRequest,
    mqDialogType,
    salesManagerEmail,
  })

  const methods = useForm<MQFields>({
    defaultValues: formContent.defaultValues,
    resolver: zodResolver(MQCommonFieldsFormSchema.merge(formContent.formSchema)),
  })

  const pickupAddress = useWatch<MQFields, 'pickupAddress'>({ name: 'pickupAddress', control: methods.control })
  const deliveryAddress = useWatch<MQFields, 'deliveryAddress'>({
    name: 'deliveryAddress',
    control: methods.control,
  })

  const onSubmit = (data: MQFields) => {
    if (data.buyersConsolidation) {
      sendAnalyticsEvent('buyers-consolidation-quote-requested')
    }
    createMQApi({ data, files })
  }

  if (isSuccess || isSubmitting) {
    return (
      <ProgressDialogContent
        email={userEmail}
        onClose={onCancelClick}
        title={formContent.headerText}
        isSubmitting={isSubmitting}
      />
    )
  }

  return (
    <FormProvider {...methods}>
      <DialogTitle showClose onClose={onCancelClick}>
        {formContent.headerText}
      </DialogTitle>
      <DialogContent>
        {formContent.summary}
        <CargoRoute from={pickupAddress} to={deliveryAddress} cargo={quoteRequest.cargo} />
        <Grid className={classes.section} container spacing={2}>
          <MQServices
            transportMode={quoteRequest.modes[0]}
            hasCustomsService={quoteRequest.customs}
            hsCodesCount={quoteRequest.hsCodesCount}
            insurance={quoteRequest.insuranceEnabled ? quoteRequest.insurance : undefined}
          />
          {formContent.services}
        </Grid>
        <Grid className={classes.section} container alignItems="center" spacing={2}>
          <MQFormCommonFields
            bookingParty={quoteRequest.bookingParty as BookingParty}
            transportModes={quoteRequest.modes}
            transportType={quoteRequest.type}
            crdHelperText={
              quoteRequest.buyersConsolidation
                ? translate('quote_request-cargo_ready-helper_text-buyers_consolidation')
                : undefined
            }
          />
          <Grid xs={12}>
            <ManualQuoteDocumentUploadComponent setterForFilesToUpload={setFiles} />
          </Grid>
          {formContent.formFields}
          <Grid xs={12}>
            <ManualQuoteAdditionalFields defaultIsExpanded={issAdditionalFieldsExpanded} />
          </Grid>
          {/* error message is shown only for Ops, so we can skip translation */}
          {manualQuoteReasonMessage && (
            <Grid xs={12}>
              <Typography variant="body1" color="error" paragraph>
                {manualQuoteReasonMessage}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelClick}>{translate('manual_quote-dont_request')}</Button>
        <Button
          startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
          disabled={isSubmitting || !methods.formState.isValid}
          variant="contained"
          onClick={methods.handleSubmit(onSubmit)}
        >
          {translate('manual_quote-request')}
        </Button>
      </DialogActions>
    </FormProvider>
  )
}

export type LeanManualQuoteDialogProps = {
  mqDialogState: ManualQuoteDialogState
  requestManualQuoteApi: (payload: ManualQuotePayload) => Promise<{ manualQuoteID: string }>
  uploadFiles: (input: DocumentInformation[], manualQuoteID: string) => Promise<void>
  userEmail: string
  salesManagerEmail?: string
  defaultFiles?: DocumentInformation[]
  issAdditionalFieldsExpanded?: boolean
}
export const LeanManualQuoteDialog: FC<LeanManualQuoteDialogProps> = ({
  mqDialogState,
  requestManualQuoteApi,
  uploadFiles,
  userEmail,
  salesManagerEmail,
  defaultFiles = [],
  issAdditionalFieldsExpanded = false,
}) => {
  const handleClose = () => {
    mqDialogState.closeManualQuoteDialog()
    createMQMutation.reset()
  }
  const analytics = useAnalytics()
  const history = useHistory()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleCreateMQApi = async ({ data, files }: CreateMQApiArgs) => {
    const { buyersConsolidation } = data
    const buyersConsolidationPayload = buyersConsolidation
      ? {
          numberOfShipments: buyersConsolidation.numberOfShipments,
          incoterms: buyersConsolidation.incoterms,
          enabled: buyersConsolidation.enabled,
        }
      : undefined

    const response = await requestManualQuoteApi({
      modes: mqDialogState.quoteRequest?.modes,
      quoteSheetId: mqDialogState.quoteSheetId as string,
      customerNotes: data.additionalInfo ?? '',
      pickupAddress: data.pickupAddress,
      deliveryAddress: data.deliveryAddress,
      cargoReadyAt: data.cargoReadyAt as Date,
      additionalRequirements: data.additionalRequirements,
      buyersConsolidation: buyersConsolidationPayload,
      incoterm: data.incoTerm,
      bookingParty: mqDialogState.quoteRequest?.bookingParty,
    })

    const { manualQuoteID } = response
    history.push('/quotes')
    uploadFiles(files, manualQuoteID)
  }
  const createMQMutation = useMutation({
    mutationFn: handleCreateMQApi,
    onError: (e) => {
      handleClose()
      if (e instanceof Error) {
        logError(e)
      }
    },
  })

  return (
    <Dialog maxWidth="md" disableEnforceFocus onClose={handleClose} fullScreen={fullScreen} open={mqDialogState.isOpen}>
      <ErrorBoundary>
        {mqDialogState.quoteRequest ? (
          <MQDialogContent
            createMQApi={createMQMutation.mutate}
            quoteRequest={mqDialogState.quoteRequest}
            manualQuoteReasonMessage={mqDialogState.manualQuoteReasonMessage}
            onCancelClick={handleClose}
            sendAnalyticsEvent={(e) => analytics.sendEvent(e)}
            isSubmitting={createMQMutation.isPending}
            mqDialogType={mqDialogState.manualQuoteType}
            isSuccess={createMQMutation.isSuccess}
            userEmail={userEmail}
            salesManagerEmail={salesManagerEmail}
            defaultFiles={defaultFiles}
            issAdditionalFieldsExpanded={issAdditionalFieldsExpanded}
          />
        ) : null}
      </ErrorBoundary>
    </Dialog>
  )
}

export const ManualQuoteDialog: FC = () => {
  const {
    API,
    authStore: {
      state: { user, customer },
    },
  } = useRootStore()
  const { mqDialogState } = useQuoteService()
  const { uploadDocumentMutation } = useManualQuoteDocumentsApi()

  const uploadFiles = async (input: DocumentInformation[], manualQuoteID: string) => {
    const promiseCollection = input.map((file) => {
      return uploadDocumentMutation.mutateAsync({
        file: file.fileInstance,
        manualQuoteID,
        json: documentUploadMetaDataMapper(file),
      })
    })
    await Promise.all(promiseCollection)
  }

  return (
    <LeanManualQuoteDialog
      mqDialogState={mqDialogState}
      requestManualQuoteApi={API.quoteSheets.requestManualQuote}
      uploadFiles={uploadFiles}
      userEmail={user?.email}
      salesManagerEmail={get(customer, 'assignees.sales.email')}
    />
  )
}

type SentDialogContentProps = {
  email: string
  onClose: () => void
  title: ReactNode
  isSubmitting?: boolean
}

const ProgressDialogContent: FC<SentDialogContentProps> = ({ email, onClose, title, isSubmitting = false }) => {
  const { translate } = useI18n()
  return (
    <>
      <DialogTitle showClose onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        {isSubmitting ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              {translate('manual_quote-quote_will_be_sent_to_email', { email })}
            </Typography>
            <Typography variant="body1" paragraph>
              {translate('manual_quote-contact_us')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isSubmitting ? null : <Button onClick={onClose}>{translate('manual_quote-close')}</Button>}
      </DialogActions>
    </>
  )
}
